import React from "react";
import {
  Box,
  // Button,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Checkbox,
  TableSortLabel,
  Link,
} from "@material-ui/core";
import {
  StyledTableCell,
  StyledTableRow,
  StickyRightTableCell,
  StickyLeftTableCell,
} from "components/table";
import { SkeletonTableRowStickyLeftRight } from "components/skeleton/skeleton.component";
import { ActionButton } from "components/button";
import { TablePaginationActions } from "components/tablePagination";
import RefreshTable from "components/table/refreshTable";
import SelectAllCheckBox from "components/selectAllCheckbox";
import clsx from "clsx";
import DefaultProfile from "assets/img/defaultPhoto.png";
import DefaultImg from "assets/img/img-default.png";
import { ROOT } from "modules/stockflow/config/stockflow.route";
import moment from "moment";
import { KeyboardArrowDown } from "@material-ui/icons";
import { TIER_ICON_PROPS } from "modules/stockflow/constants";
import { getLang } from "app/feature/constants";
import "moment/min/locales.min";
// const API_HOST = process.env.REACT_APP_API_HOST_URL

const useStyle = makeStyles((theme) => ({
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      borderRight: "1px solid rgba(58, 77, 84, 0.1)",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 37,
      borderRight: "0px",
    },
    "& th th": {
      borderBottom: "none !important",
    },
    "& td td": {
      borderBottom: "none !important",
    },
  },
  tableContainer: {
    maxHeight: 500,
    overflowY: "auto",
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
  },
  button: {
    display: "flex",
    alignItems: "center",
    padding: 0,
    minWidth: 0,
    marginLeft: theme.spacing(1),
  },
  productPhoto: {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #E4E7EC",
    width: "30px",
    height: "30px",
    marginRight: "10px",
    "& img": {
      height: "30px",
      width: "30px",
    },
  },
  actionContainer: {
    justifyContent: "center",
  },
  paperRoot: {
    width: "300px",
  },
  selectAll: {
    "&.Mui-checked": {
      color: "#FDB022",
    },
  },
  selectOne: {
    "&.Mui-checked": {
      color: "#6AAF68",
    },
  },
  activeText: {
    color: "#32D583",
  },
  suspendText: {
    color: "#F97066",
  },
  profile: {
    height: 26,
    width: 26,
    minWidth: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& img": {
      height: "100%",
      minHeight: "100%",
      minWidth: "100%",
      objectFit: "cover",
      borderRadius: "50%",
      border: "1px solid #D0D5DD",
    },
  },
  tierIcon: {
    borderRadius: "3px",
    paddingLeft: "0.15em",
    paddingRight: "0.15em",
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: "-3px",
    left: "-8px",
  },
  tierLabel: {
    color: "#FFFFFF",
    fontSize: "0.6rem",
  },
  attributeText: {
    color: "#b2b2b2",
    fontSize: 12,
    whiteSpace: "break-spaces",
  },
}));

export default function PackageListTableComponent({
  hasStockflowInventoryViewAccessRight,
  hasStockflowDealerViewAccessRight,
  rows,
  isFetching,
  language,
  isSelectAll,
  selectedIds,
  isError,
  handleReload,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowPerPage,
  isSearchResult,
  totalFiltered,
  handleSelectAvailable,
  handleSelectAll,
  handleDeselectAll,
  handleSelectBox,
  token,
  paginationOptions,
  tableFilter,
  changeOrder,
  lang,
  isDealerPage,
  dealerId,
  handleViewDetail,
}) {
  const classes = useStyle();

  const selectAllItems = [
    {
      label: getLang(lang, "paragraph.SELECT_AVAILABLE_ITEM", {
        count: rows.length > rowsPerPage ? rowsPerPage : rows.length,
      }),
      action: () => handleSelectAvailable(),
    },
    {
      label: getLang(lang, "paragraph.SELECT_ALL_BOXES"),
      action: () => handleSelectAll(),
    },
    {
      label: getLang(lang, "paragraph.DESELECT_ALL_BOXES"),
      action: () => handleDeselectAll(),
      divider: true,
    },
  ];

  return (
    <>
      {!isFetching && isError && <RefreshTable handleReload={handleReload} />}
      {isFetching || rows.length > 0 ? (
        <Box>
          <TableContainer>
            <Table
              className={classes.table}
              aria-label="package table"
              size="small"
              stickyHeader
            >
              <TableHead style={{ height: "45px" }}>
                <TableRow>
                  {!isDealerPage && (
                    <StickyLeftTableCell>
                      <StyledTableCell padding="checkbox">
                        <SelectAllCheckBox
                          actionItems={selectAllItems}
                          disabled={isFetching}
                          isSelectAll={isSelectAll}
                          selectedIdLength={selectedIds.length}
                          totalFiltered={totalFiltered}
                        />
                      </StyledTableCell>
                    </StickyLeftTableCell>
                  )}
                  <StyledTableCell>
                    {getLang(lang, "label.BOX_CODE")}
                  </StyledTableCell>
                  <StyledTableCell style={{ minWidth: "20rem" }}>
                    {getLang(lang, "label.PRODUCTS")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.QUANTITY")}
                  </StyledTableCell>
                  {!isDealerPage && (
                    <StyledTableCell>
                      {getLang(lang, "label.LOCATED_AT")}
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    <TableSortLabel
                      active={tableFilter.orderBy === "created_at"}
                      direction={tableFilter.order}
                      onClick={() => {
                        const differentTab =
                          tableFilter.orderBy !== "created_at";
                        const order = differentTab
                          ? tableFilter.order
                          : tableFilter.order === "asc"
                          ? "desc"
                          : "asc";
                        changeOrder("created_at", order);
                      }}
                      IconComponent={KeyboardArrowDown}
                    >
                      {getLang(
                        lang,
                        isDealerPage
                          ? "label.RECEIVED_DATE"
                          : "label.CREATED_DATE"
                      )}
                    </TableSortLabel>
                  </StyledTableCell>
                  {!isDealerPage && (
                    <>
                      <StyledTableCell>
                        <TableSortLabel
                          active={tableFilter.orderBy === "updated_at"}
                          direction={tableFilter.order}
                          onClick={() => {
                            const differentTab =
                              tableFilter.orderBy !== "updated_at";
                            const order = differentTab
                              ? tableFilter.order
                              : tableFilter.order === "asc"
                              ? "desc"
                              : "asc";
                            changeOrder("updated_at", order);
                          }}
                          IconComponent={KeyboardArrowDown}
                        >
                          {getLang(lang, "label.LAST_UPDATED_DATE")}
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell>
                        {getLang(lang, "label.BRANCH_ASSIGNED")}
                      </StyledTableCell>
                      <StickyRightTableCell>
                        <StyledTableCell
                          style={{ width: "67px", textAlign: "center" }}
                        >
                          {getLang(lang, "label.ACTION")}
                        </StyledTableCell>
                      </StickyRightTableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching
                  ? [...new Array(5)].map((v, index) => (
                      <SkeletonTableRowStickyLeftRight
                        key={index}
                        columnCount={isDealerPage ? 4 : 9}
                      />
                    ))
                  : rows.map((row, index) => {
                      const actionItems = [];

                      actionItems.push({
                        label: getLang(lang, "label.VIEW_DETAILS"),
                        action: () => {
                          let url = `${ROOT}/inventory/box/${row.id}`;

                          if (isDealerPage && dealerId) {
                            url += `?dealer=${dealerId}`;
                          }
                          handleViewDetail(url);
                        },
                      });

                      return (
                        <StyledTableRow
                          key={row.id}
                          hover
                          role="checkbox"
                          tabIndex={-1}
                        >
                          {!isDealerPage && (
                            <StickyLeftTableCell>
                              <StyledTableCell padding="checkbox">
                                <Checkbox
                                  className={clsx({
                                    [classes.selectAll]: isSelectAll,
                                    [classes.selectOne]:
                                      row.isSelected && isSelectAll === false,
                                  })}
                                  checked={row.isSelected}
                                  onChange={(event) =>
                                    handleSelectBox(index, event.target.checked)
                                  }
                                />
                              </StyledTableCell>
                            </StickyLeftTableCell>
                          )}
                          <StyledTableCell>
                            {row.code ? (
                              hasStockflowInventoryViewAccessRight ? (
                                <Link
                                  href={`${ROOT}/inventory/box/${row.id}`}
                                  color="secondary"
                                  variant="caption"
                                  onClick={(e) => {
                                    handleViewDetail(
                                      `${ROOT}/inventory/box/${row.id}`
                                    );
                                    e.preventDefault();
                                    return false;
                                  }}
                                >
                                  {row.code}
                                </Link>
                              ) : (
                                row.code
                              )
                            ) : (
                              "-"
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.products.length > 0 ? (
                              row.products.map((p, index) => (
                                <Box
                                  key={p.id}
                                  display="flex"
                                  alignItems="center"
                                  mt={index !== 0 ? 1 : 0}
                                >
                                  <Box className={classes.productPhoto}>
                                    {p.picture === "" ? (
                                      <img src={DefaultImg} alt="emptyProduct" />
                                    ) : (
                                      <img src={p.picture} alt={p.name} />
                                    )}
                                  </Box>
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="flex-start"
                                  >
                                    <Typography
                                      variant="body2"
                                      style={{ fontSize: 12 }}
                                    >
                                      {p.name}
                                    </Typography>
                                    {!!p.attributes && !!p.attributes.length && (
                                      <Typography
                                        className={classes.attributeText}
                                        variant="body2"
                                      >
                                        {p.attributes.join(", ")}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))
                            ) : (
                              <span>-</span>
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.products.length > 0 ? (
                              row.products.map((p, index) => (
                                <Box
                                  mt={index !== 0 ? 1 : 0}
                                  minHeight={32}
                                  display="flex"
                                  justifyContent="flex-end"
                                  alignItems="center"
                                >
                                  <span>
                                    {(p.quantity || 0).toLocaleString("en-US")}
                                  </span>
                                </Box>
                              ))
                            ) : (
                              <Box  
                                minHeight={32}
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                <span>0</span>
                              </Box>
                            )}
                          </StyledTableCell>
                          {!isDealerPage && (
                            <StyledTableCell>
                              {row.located_at ? (
                                <Box display="flex" alignItems="center">
                                  <Box
                                    className={classes.profile}
                                    mr={1}
                                    ml={0.5}
                                    position="relative"
                                  >
                                    {row.located_at.picture === "" ? (
                                      <img
                                        src={
                                          row.located_at.type === "branch"
                                            ? DefaultImg
                                            : DefaultProfile
                                        }
                                        alt="default profile"
                                      />
                                    ) : (
                                      <img
                                        src={row.located_at.picture}
                                        alt={row.name}
                                      />
                                    )}
                                    {!!row.located_at.tier && (
                                      <Box
                                        className={classes.tierIcon}
                                        style={{
                                          backgroundColor: TIER_ICON_PROPS.find(
                                            ({ tier }) =>
                                              tier === row.located_at.tier
                                          ).color,
                                        }}
                                      >
                                        <Typography
                                          className={classes.tierLabel}
                                        >
                                          T{row.located_at.tier}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  {row.located_at.type === "branch" ||
                                  !hasStockflowDealerViewAccessRight ? (
                                    <Typography variant="caption">
                                      {row.located_at.name}
                                    </Typography>
                                  ) : (
                                    <Link
                                      href={
                                        row.located_at.type === "dealer"  
                                        ? `${ROOT}/my-dealer/${row.located_at.uuid}`
                                        : '#'
                                      }
                                      color="secondary"
                                      onClick={(e) => {
                                        if (row.located_at.type === "dealer") {
                                          handleViewDetail(
                                            `${ROOT}/my-dealer/${row.located_at.uuid}`
                                          );
                                        }
                                        e.preventDefault();
                                        return false;
                                      }}
                                    >
                                      <Typography variant="caption">
                                        {row.located_at.name}
                                      </Typography>
                                    </Link>
                                  )}
                                </Box>
                              ) : (
                                <span>-</span>
                              )}
                            </StyledTableCell>
                          )}
                          <StyledTableCell>
                            <span>
                              {moment.utc(row.created_at).local().format("lll")}
                            </span>
                          </StyledTableCell>
                          {!isDealerPage && (
                            <>
                              <StyledTableCell>
                                <span>
                                  {moment
                                    .utc(row.updated_at)
                                    .local()
                                    .format("lll")}
                                </span>
                              </StyledTableCell>
                              <StyledTableCell>
                                <span>{row.node?.name || "-"}</span>
                              </StyledTableCell>
                              <StickyRightTableCell>
                                <StyledTableCell style={{ width: "67px" }}>
                                  <ActionButton
                                    actionItems={actionItems}
                                    popperStyle={{ zIndex: 999 }}
                                  />
                                </StyledTableCell>
                              </StickyRightTableCell>
                            </>
                          )}
                        </StyledTableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            style={{
              backgroundColor: "#fff",
              boxShadow: "0px -1px 0px rgba(58, 77, 84, 0.15)",
            }}
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={paginationOptions}
            component="div"
            count={totalFiltered}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowPerPage}
            labelRowsPerPage={getLang(lang, 'label.ROWS_PER_PAGE')}
            labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString("en-US")}-${to.toLocaleString("en-US")} ${getLang(lang, 'label.OF')} ${count.toLocaleString("en-US")}`}
          />
        </Box>
      ) : null}
      {!isFetching && !isError && rows.length === 0 && (
        <Box>
          <TableContainer>
            <Table
              className={classes.table}
              aria-label="box table"
              size="small"
              stickyHeader
            >
              <TableHead style={{ height: "45px" }}>
                <TableRow>
                  {!isDealerPage && (
                    <StickyLeftTableCell style={{ textAlign: "center" }}>
                      <StyledTableCell padding="checkbox">
                        <SelectAllCheckBox disabled={true} />
                      </StyledTableCell>
                    </StickyLeftTableCell>
                  )}
                  <StyledTableCell>
                    {getLang(lang, "label.BOX_CODE")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.PRODUCTS")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.QUANTITY")}
                  </StyledTableCell>
                  {!isDealerPage && (
                    <StyledTableCell>
                      {getLang(lang, "label.LOCATED_AT")}
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    {getLang(lang, "label.CREATED_DATE")}
                  </StyledTableCell>
                  {!isDealerPage && (
                    <>
                      <StyledTableCell>
                        {getLang(lang, "label.LAST_UPDATED_DATE")}
                      </StyledTableCell>
                      <StyledTableCell>
                        {getLang(lang, "label.BRANCH_ASSIGNED")}
                      </StyledTableCell>
                      <StickyRightTableCell>
                        <StyledTableCell>
                          {getLang(lang, "label.ACTION")}
                        </StyledTableCell>
                      </StickyRightTableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <Box py={9} className={classes.emptyContainer}>
            <Typography
              variant="body1"
              style={{ paddingBottom: "16px", fontWeight: "bold" }}
            >
              {isSearchResult || isDealerPage
                ? getLang(lang, "paragraph.NO_BOX_EXISTS")
                : getLang(lang, "paragraph.NO_BOX_CREATED")}
            </Typography>
            {!isSearchResult && !isDealerPage && (
              <>
                <Typography
                  variant="subtitle2"
                  style={{ paddingBottom: "16px" }}
                >
                  {getLang(lang, "paragraph.INVENTORY_BOX_INSTRUCTION")}
                </Typography>
                {/* <Button
                  onClick={() => {
                    // TODO: add link
                  }}
                  variant="contained"
                  disableElevation
                  color="primary"
                >
                  {getLang(lang, "label.LEARN_HOW")}
                </Button> */}
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
